/* src/pages/Donation/StepPages/Successful/index.jsx */

import React, { useEffect } from "react";
import "./index.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Confetti from "react-confetti";

import {
  successPageVariants,
  successIconVariants,
  successTextVariants,
  successButtonVariants
} from "../../../../assets/framer/styles";

import SuccessIcon from "../../../../assets/images/success-icon.svg";
import CustomButton from "../../../../components/CustomButton";
import { trackGtmEvent } from "../../../../analytics/GtmProvider";
import { getCanonicalTemplateKey, isFridayTemplateKey } from "../../../../utils/templateUtils";

// 1) Import i18n
import { useTranslation } from "react-i18next";

function toTwoDecimals(value, { truncate = false } = {}) {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(num)) return 0;
  return truncate ? Math.floor(num * 100) / 100 : Math.round(num * 100) / 100;
}

const Successful = () => {
  // 2) Destructure `t` from `useTranslation`
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const template = getCanonicalTemplateKey(searchParams.get("template"));
  const scheduleId = searchParams.get("scheduleId");
  const pendingScheduleId = searchParams.get("pendingScheduleId");

  const tipAmount = parseFloat(searchParams.get("tipAmount") || "0") || 0;
  const processingFee = parseFloat(searchParams.get("processingFee") || "0") || 0;

  const breakdownParam = searchParams.get("breakdown");
  let amountSum = 0;
  if (breakdownParam) {
    try {
      const breakdown = JSON.parse(decodeURIComponent(breakdownParam));
      amountSum = breakdown.reduce((sum, item) => sum + (item.amount || 0), 0);
    } catch (error) {
      amountSum = 0;
    }
  }
  const grandTotal = amountSum + tipAmount + processingFee;

  const isFridayTemplate = isFridayTemplateKey(template);

  useEffect(() => {
    const conversionId = scheduleId || pendingScheduleId;

    if (!conversionId) {
      return;
    }

    const storageKey = `gtm_donation_success_${conversionId}`;
    try {
      if (window.sessionStorage.getItem(storageKey)) {
        return;
      }
    } catch (error) {
      // If sessionStorage is blocked, still attempt to fire conversion event.
    }

    trackGtmEvent("donation_success", {
      value: toTwoDecimals(amountSum, { truncate: true }),
      grand_total: toTwoDecimals(grandTotal),
      currency: "CAD",
      tip_amount: toTwoDecimals(tipAmount),
      processing_fee: toTwoDecimals(processingFee),
      schedule_id: scheduleId || "",
      pending_schedule_id: pendingScheduleId || "",
      template: (template || "").toLowerCase()
    });

    try {
      window.sessionStorage.setItem(storageKey, "1");
    } catch (error) {
      // No-op if storage is unavailable.
    }
  }, [
    amountSum,
    grandTotal,
    pendingScheduleId,
    processingFee,
    scheduleId,
    template,
    tipAmount
  ]);

  const handleBackToHome = () => {
    if (template) {
      navigate(`/donate?template=${template}`);
    } else {
      navigate("/donate");
    }
  };
  const handleViewDonation = () => {
    navigate(`/manage-donation/${scheduleId}`);
  };

  return (
    <>
      <Confetti numberOfPieces={300} recycle={false} />
      <motion.div
        className="successful-container"
        variants={successPageVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          className="successful-content"
          variants={successPageVariants}
        >
          <motion.div className="icon-wrapper" variants={successIconVariants}>
            <img src={SuccessIcon} alt="Success Icon" />
          </motion.div>

          {/* 3) Use translation keys for text */}
          <motion.h2 className="success-title" variants={successTextVariants}>
            {isFridayTemplate
              ? t("successDonationTitleFriday")
              : t("successDonationTitle")}
          </motion.h2>

          <motion.p className="success-message" variants={successTextVariants}>
            {isFridayTemplate
              ? t("successDonationMessageFriday")
              : t("successDonationMessage")}
          </motion.p>

          {scheduleId && (
            <motion.p
              className="success-message"
              variants={successTextVariants}
              style={{ marginTop: "10px" }}
            >
              {t("successDonationSecretLink")} <br />
              <a
                href={`${process.env.REACT_APP_BASE_URL}/manage-donation/${scheduleId}`}
              >
                {`${process.env.REACT_APP_BASE_URL}/manage-donation/${scheduleId}`}
              </a>
            </motion.p>
          )}

          {isFridayTemplate ? (
            <motion.div variants={successButtonVariants}>
              <CustomButton
                text={t("successDonationButtonHomeFriday")}
                onClick={handleViewDonation}
              />
            </motion.div>
          ) : (
            <motion.div variants={successButtonVariants}>
              <CustomButton
                text={t("successDonationButtonHome")}
                onClick={handleBackToHome}
              />
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </>
  );
};

export default Successful;
